import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { ProductCard } from "@/components/store/ProductCard";
import { CartDrawer } from "@/components/store/CartDrawer";
import { Starfield } from "@/components/Starfield";
import { Input } from "@/components/ui/input";
import { ShoppingBag, Search, Moon } from "lucide-react";
import { toast } from "sonner";

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Tous");
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [zones, setZones] = useState([]);
  const navigate = useNavigate();
  const longPressTimer = useRef(null);

  const startLongPress = () => {
    longPressTimer.current = setTimeout(() => navigate("/admin"), 650);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/settings"), api.get("/zones")])
      .then(([p, s, z]) => {
        setProducts(p.data);
        setSettings(s.data);
        setZones(z.data);
      })
      .catch(() => toast.error("Impossible de charger la boutique"))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const present = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));
    const saved = settings?.category_order || [];
    const ordered = [
      ...saved.filter((c) => present.includes(c)),
      ...present.filter((c) => !saved.includes(c)),
    ];
    return ["Tous", ...ordered];
  }, [products, settings]);

  const filtered = products.filter((p) => {
    const matchCat = category === "Tous" || p.category === category;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const cartItems = Object.values(cart);
  const cartCount = cartItems.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

  const addToCart = (product) => {
    setCart((c) => {
      const existing = c[product.id];
      return { ...c, [product.id]: { ...product, quantity: (existing?.quantity || 0) + 1 } };
    });
    toast.success(`${product.name} ajouté au panier`);
  };
  const setQty = (id, qty) => {
    setCart((c) => {
      if (qty <= 0) {
        const { [id]: _, ...rest } = c;
        return rest;
      }
      return { ...c, [id]: { ...c[id], quantity: qty } };
    });
  };
  const removeItem = (id) =>
    setCart((c) => {
      const { [id]: _, ...rest } = c;
      return rest;
    });
  const clearCart = () => setCart({});

  return (
    <div className="min-h-screen grain">
      <Starfield />
      <div className="relative z-10">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div
            className="flex items-center gap-2 select-none cursor-pointer"
            data-testid="admin-longpress-logo"
            onMouseDown={startLongPress}
            onMouseUp={cancelLongPress}
            onMouseLeave={cancelLongPress}
            onTouchStart={startLongPress}
            onTouchEnd={cancelLongPress}
            onTouchCancel={cancelLongPress}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="h-9 w-9 rounded-full brand-terracotta flex items-center justify-center">
              <Moon className="h-5 w-5 twinkle" />
            </div>
            <div className="leading-none">
              {settings ? (
                <p className="font-heading text-lg font-bold" data-testid="store-name">{settings.store_name}</p>
              ) : (
                <span className="inline-block h-5 w-36 rounded bg-muted animate-pulse" />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="open-cart-button"
              onClick={() => setCartOpen(true)}
              className="relative brand-terracotta text-white rounded-full h-10 px-4 flex items-center gap-2 font-semibold active:scale-95 transition-transform"
            >
              <ShoppingBag className="h-5 w-5" />
              <span className="hidden sm:inline">Panier</span>
              {cartCount > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1.5 -right-1.5 bg-[#0A1026] text-white text-[11px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center"
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-6">
        <p className="text-xs font-semibold tracking-widest uppercase text-terracotta">Boutique en ligne</p>
        {settings ? (
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-2 max-w-2xl" data-testid="store-tagline">
            {settings.tagline}
          </h1>
        ) : (
          <div className="mt-3 space-y-2 max-w-2xl" aria-hidden>
            <div className="h-9 sm:h-12 w-4/5 rounded bg-muted animate-pulse" />
            <div className="h-9 sm:h-12 w-1/2 rounded bg-muted animate-pulse" />
          </div>
        )}
        <p className="text-muted-foreground mt-3 max-w-xl">
          Choisissez vos produits, ajoutez-les au panier et validez votre commande directement sur WhatsApp.
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 sticky top-16 z-30 py-3 bg-background/85 backdrop-blur-md">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit…"
              className="pl-9 rounded-full bg-card"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`category-${c}`}
                onClick={() => setCategory(c)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  category === c
                    ? "brand-terracotta text-white border-transparent"
                    : "bg-card border-border hover:bg-muted"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-28">
        {loading ? (
          <p className="text-center text-muted-foreground py-20">Chargement…</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-20" data-testid="empty-state">
            Aucun produit trouvé.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filtered.map((p, idx) => (
              <ProductCard
                key={p.id}
                product={p}
                index={idx}
                inCartQty={cart[p.id]?.quantity || 0}
                onAdd={addToCart}
              />
            ))}
          </div>
        )}
      </main>

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        items={cartItems}
        setQty={setQty}
        removeItem={removeItem}
        clearCart={clearCart}
        settings={settings}
        zones={zones}
      />

      {/* Floating cart bar (mobile-first) */}
      {cartCount > 0 && !cartOpen && (
        <button
          data-testid="floating-cart-bar"
          onClick={() => setCartOpen(true)}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md brand-terracotta text-white rounded-full shadow-2xl h-14 px-5 flex items-center justify-between gap-3 active:scale-[0.98] transition-transform float-up"
          style={{ boxShadow: "0 14px 36px -10px rgba(255,213,74,0.55)" }}
        >
          <span className="flex items-center gap-2 font-semibold">
            <span className="relative">
              <ShoppingBag className="h-6 w-6" />
              <span
                data-testid="floating-cart-count"
                className="absolute -top-2 -right-2 bg-[#0A1026] text-white text-[11px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center"
              >
                {cartCount}
              </span>
            </span>
            Voir le panier
          </span>
          <span className="font-heading text-lg font-bold">{formatPrice(cartTotal)}</span>
        </button>
      )}
      </div>
    </div>
  );
}
